/**
 * Vehicle Fitment Filter – Collection Page Notice Bar
 *
 * Reads the active vehicle from localStorage and injects a notice bar
 * on collection pages. The bar lets the user quickly filter the collection
 * by their vehicle using Shopify's native tag URL:
 *   /collections/{handle}/{make}+{model}+{year}
 *
 * Tag convention (apply to products in Shopify admin), one plain tag each:
 *   Audi   A3   2016   Auto (AM-S6), 4 cyl, 2.0 L, Turbo
 *   (model/engine must match the selector's option text; add one year tag per
 *    compatible year and one engine tag per compatible engine)
 *
 * No extra dependencies – plain ES5, reads VehicleSelector if available.
 */
(function () {
  'use strict';

  // Loaded from several templates (e.g. theme.liquid and main-list-collections on /collections).
  if (window.__vsFitmentFilterLoaded) return;
  window.__vsFitmentFilterLoaded = true;

  function escHtml(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  var ACTIVE_KEY    = 'vs_active_vehicle';
  var STORAGE_KEY   = 'vs_vehicles';
  var DISMISS_KEY   = 'vs_fitment_bar_dismissed';
  var NHTSA_BASE    = 'https://vpic.nhtsa.dot.gov/api/vehicles';
  var FUEL_ECO_BASE = 'https://www.fueleconomy.gov/ws/rest/vehicle/menu';

  // ── Edit-modal API helpers ─────────────────────────────────────────────────
  function emFetchModelNames(url) {
    return fetch(url)
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        var names = (j.Results || []).map(function (x) { return x.Model_Name; });
        return names.filter(function (v, i, a) { return a.indexOf(v) === i; }).sort();
      });
  }

  // Whitelist from the "Vehicle Selector Settings" section (no-op if vehicle-data.js is absent).
  function rulesApi() {
    var d = window.VehicleData;
    return d && d.filterModels ? d : null;
  }

  function emFetchModels(make, year) {
    var configured = rulesApi() && rulesApi().configuredModels(make, year);
    if (configured) return Promise.resolve(configured);
    var all = NHTSA_BASE + '/GetModelsForMake/' + encodeURIComponent(make) + '?format=json';
    var request = !year ? emFetchModelNames(all) :
      emFetchModelNames(NHTSA_BASE + '/GetModelsForMakeYear/make/' + encodeURIComponent(make) +
          '/modelyear/' + encodeURIComponent(year) + '?format=json')
        .then(function (names) { return names.length ? names : emFetchModelNames(all); });
    return request.catch(function (err) {
      if (rulesApi() && rulesApi().filterModels(make, [], year).length) return [];
      throw err;
    }).then(function (names) {
      return rulesApi() ? rulesApi().filterModels(make, names, year) : names;
    });
  }

  // fueleconomy.gov returns a bare object instead of an array when there is a single result.
  function emMenuItems(j) {
    var m = j && j.menuItem;
    return Array.isArray(m) ? m : (m ? [m] : []);
  }

  function emFetchTrims(year, make, model) {
    return fetch(FUEL_ECO_BASE + '/model?year=' + encodeURIComponent(year) + '&make=' + encodeURIComponent(make), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        var norm = model.toLowerCase();
        var trims = emMenuItems(j).map(function (x) { return x.value; })
          .filter(function (v) { return v.toLowerCase().indexOf(norm) === 0; });
        return rulesApi() ? rulesApi().filterTrims(make, trims, year, model) : trims;
      });
  }

  function emFetchEngines(year, make, trim) {
    return fetch(FUEL_ECO_BASE + '/options?year=' + encodeURIComponent(year) + '&make=' + encodeURIComponent(make) + '&model=' + encodeURIComponent(trim), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        return emMenuItems(j).map(function (x) { return x.text; });
      });
  }

  // Trim is hidden on the storefront, so offer the engines of every (allowed) trim of the model.
  function emFetchEnginesForModel(year, make, model) {
    var configured = rulesApi() && rulesApi().configuredEngines(make, year, model);
    if (configured) return Promise.resolve(configured);
    return emFetchTrims(year, make, model)
      .catch(function () { return []; })
      .then(function (trims) {
        return Promise.all(trims.map(function (t) {
          return emFetchEngines(year, make, t).catch(function () { return []; });
        }));
      })
      .then(function (lists) {
        var seen = {}, out = [];
        lists.forEach(function (list) {
          list.forEach(function (e) { if (!seen[e]) { seen[e] = true; out.push(e); } });
        });
        return rulesApi() ? rulesApi().filterEngines(make, out.sort(), year, model) : out.sort();
      });
  }

  function emGenerateYears() {
    var years = [];
    for (var y = new Date().getFullYear() + 1; y >= 1990; y--) years.push(String(y));
    return rulesApi() ? rulesApi().filterYears(years) : years;
  }

  function emPopulate(sel, options, placeholder) {
    sel.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = ''; ph.disabled = true; ph.selected = true;
    ph.textContent = placeholder;
    sel.appendChild(ph);
    options.forEach(function (o) {
      var el = document.createElement('option');
      el.value = el.textContent = o;
      sel.appendChild(el);
    });
    sel.disabled = false;
  }

  function emReset(sel, placeholder) {
    sel.innerHTML = '<option value="" disabled selected>' + placeholder + '</option>';
    sel.disabled = true;
  }

  function emLoading(sel) {
    sel.innerHTML = '<option value="" disabled selected>Loading…</option>';
    sel.disabled = true;
  }

  function getSaved() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }
  function setSaved(list) { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); }
  function setActive(v) {
    if (v) localStorage.setItem(ACTIVE_KEY, JSON.stringify(v));
    else   localStorage.removeItem(ACTIVE_KEY);
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  function getActive() {
    try { return JSON.parse(localStorage.getItem(ACTIVE_KEY)) || null; }
    catch (e) { return null; }
  }

  /** Mirrors Shopify's handleize, i.e. how a tag appears in a collection URL. */
  function toSlug(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function vehicleTagSlugs(vehicle) {
    return [vehicle.make, vehicle.model, vehicle.year, vehicle.engine].map(toSlug).filter(Boolean);
  }

  /** Build the tag-filtered collection URL. */
  function buildTagUrl(collectionBase, vehicle) {
    // Preserve query string (e.g. ?preview_theme_id=...) on navigation
    var qs = window.location.search;
    return collectionBase + '/' + vehicleTagSlugs(vehicle).join('+') + qs;
  }

  // Read by collection-product-grid.liquid: Shopify redirects away unknown tags on the
  // primary domain, so the landing page can't see what was requested otherwise.
  function rememberRequestedFilter(url) {
    var m = String(url).match(/\/collections\/([^/?#]+)\/([^/?#]+)/);
    if (!m) return;
    try {
      sessionStorage.setItem('vs_requested_filter', JSON.stringify({ handle: m[1], tags: m[2], at: Date.now() }));
    } catch (e) {}
  }

  /** Check if the current URL already contains the vehicle's tag filter. */
  function isAlreadyFiltered(vehicle) {
    // Unmatched vehicle filter: Shopify dropped the tags from the URL, but the grid shows "no products".
    if (document.querySelector('.vs-tag-unmatched')) return true;
    var m = window.location.pathname.match(/^\/collections\/[^/]+\/([^/?#]+)/);
    if (!m) return false;
    var current = decodeURIComponent(m[1]).split('+');
    return vehicleTagSlugs(vehicle).every(function (s) { return current.indexOf(s) !== -1; });
  }

  /** Get the base collection URL (handle only, no tag suffix). */
  function getCollectionBase() {
    var match = window.location.pathname.match(/^(\/collections\/[^/]+)/);
    return match ? match[1] : null;
  }

  // ── car SVG helper ──────────────────────────────────────────────────────────

  function carSVG() {
    return '<svg viewBox="0 0 40 40" width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M32.637 12.307h4.03v2.538h-2.84l1.224 1.347A5.292 5.292 0 0 1 36.667 20v11.07a2.265 2.265 0 0 1-2.263 2.263h-7.31a2.265 2.265 0 0 1-2.262-2.262v-1.726H14.814v1.726c0 1.247-.661 2.262-1.909 2.262h-7.31a2.265 2.265 0 0 1-2.262-2.262V20c0-1.484.615-2.84 1.615-3.808l1.49-1.347H3.333v-2.538h4.12l.807-1.69a5.3 5.3 0 0 1 5.123-3.95h13.39a5.32 5.32 0 0 1 3.28 1.137 5.32 5.32 0 0 1 1.871 2.922l.713 1.58Zm-.898 4.92-.852-.111-21.75.006-.845.102A2.795 2.795 0 0 0 5.834 20v1.082h6.494v2.502H5.834v7.248h6.832v-3.989h14.667v3.989h6.832v-7.248h-6.493v-2.502h6.493V20a2.802 2.802 0 0 0-2.426-2.772ZM9 14.66h22.082l-1.589-3.35a2.788 2.788 0 0 0-2.719-2.142h-13.39c-1.27 0-2.381.858-2.704 2.086l-1.68 3.406Z" fill="currentColor"/>' +
    '</svg>';
  }

  // ── vehicle modal ─────────────────────────────────────────────────────────

  // ── Edit Vehicle Modal ─────────────────────────────────────────────────────
  function openEditVehicleModal(vehicle) {
    var existing = document.getElementById('vs-edit-modal-overlay');
    if (existing) existing.parentNode.removeChild(existing);

    var makes = [];
    if (typeof VehicleData !== 'undefined' && VehicleData.getMakes) {
      makes = VehicleData.getMakes();
    } else {
      // vehicle-data.js not yet loaded – wait for it via DOMContentLoaded fallback
      document.addEventListener('vehicleDataReady', function handler() {
        document.removeEventListener('vehicleDataReady', handler);
        openEditVehicleModal(vehicle);
      });
      // Also try polling in case event already fired
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        if (typeof VehicleData !== 'undefined' && VehicleData.getMakes) {
          clearInterval(poll);
          openEditVehicleModal(vehicle);
        } else if (attempts > 20) {
          clearInterval(poll);
          // open without makes list
          openEditVehicleModalWithMakes(vehicle, []);
        }
      }, 100);
      return;
    }
    openEditVehicleModalWithMakes(vehicle, makes);
  }

  function openEditVehicleModalWithMakes(vehicle, makes) {
    var existing = document.getElementById('vs-edit-modal-overlay');
    if (existing) existing.parentNode.removeChild(existing);
    var years = emGenerateYears();

    var overlay = document.createElement('div');
    overlay.id  = 'vs-edit-modal-overlay';
    overlay.className = 'vs-edit-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML =
      '<div class="vs-edit-modal">' +
        '<div class="vs-edit-modal__header">' +
          '<h3 class="vs-edit-modal__title">Edit Vehicle</h3>' +
          '<button class="vs-edit-modal__close" aria-label="Close">' +
            '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="vs-edit-modal__body">' +
          '<div class="vs-edit-modal__tabs">' +
            '<button class="vs-edit-modal__tab vs-edit-modal__tab--active" data-panel="mm">Make/Model</button>' +
            '<button class="vs-edit-modal__tab" data-panel="vin">VIN</button>' +
          '</div>' +
          '<div id="vs-edit-panel-mm" class="vs-edit-modal__panel vs-edit-modal__panel--active">' +
            '<div class="vs-edit-modal__selects">' +
              '<div class="vs-edit-modal__select-wrap"><select id="vs-edit-year" class="vs-edit-modal__select"></select></div>' +
              '<div class="vs-edit-modal__select-wrap"><select id="vs-edit-make" class="vs-edit-modal__select"></select></div>' +
              '<div class="vs-edit-modal__select-wrap"><select id="vs-edit-model" class="vs-edit-modal__select"></select></div>' +
              '<div class="vs-edit-modal__select-wrap" style="display:none"><select id="vs-edit-trim" class="vs-edit-modal__select"></select></div>' +
              '<div class="vs-edit-modal__select-wrap"><select id="vs-edit-engine" class="vs-edit-modal__select"></select></div>' +
            '</div>' +
          '</div>' +
          '<div id="vs-edit-panel-vin" class="vs-edit-modal__panel" style="display:none">' +
            '<div class="vs-edit-modal__vin-wrap">' +
              '<input id="vs-edit-vin" class="vs-edit-modal__vin-input" type="text" maxlength="17" placeholder="Enter 17-digit VIN" />' +
              '<button class="vs-edit-modal__vin-btn">Decode VIN</button>' +
            '</div>' +
            '<p class="vs-edit-modal__vin-result"></p>' +
          '</div>' +
        '</div>' +
        '<div class="vs-edit-modal__footer">' +
          '<button class="vs-edit-modal__save" id="vs-edit-save">SAVE</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    var selYear   = overlay.querySelector('#vs-edit-year');
    var selMake   = overlay.querySelector('#vs-edit-make');
    var selModel  = overlay.querySelector('#vs-edit-model');
    var selTrim   = overlay.querySelector('#vs-edit-trim');
    var selEngine = overlay.querySelector('#vs-edit-engine');
    var btnSave   = overlay.querySelector('#vs-edit-save');

    // init Year
    emPopulate(selYear, years, 'Year *');
    // init Make
    if (rulesApi() && vehicle && vehicle.year) makes = rulesApi().getMakes(vehicle.year);
    emPopulate(selMake, makes, 'Make *');
    // reset downstream
    emReset(selModel,  'Model *');
    emReset(selTrim,   'Trim');
    emReset(selEngine, 'Engine');

    function updateSaveBtn() {
      btnSave.disabled = !(selYear.value && selMake.value && selModel.value);
    }

    // prefill
    if (vehicle) {
      if (vehicle.year) selYear.value = vehicle.year;
      if (vehicle.make) selMake.value = vehicle.make;
      if (selMake.value) {
        emLoading(selModel);
        emFetchModels(vehicle.make, vehicle.year)
          .then(function (models) {
            emPopulate(selModel, models, 'Model *');
            if (vehicle.model) { selModel.value = vehicle.model; selModel.disabled = false; }
            updateSaveBtn();
            if (!selModel.value || !vehicle.year) return null;
            emLoading(selEngine);
            return emFetchEnginesForModel(vehicle.year, vehicle.make, vehicle.model);
          })
          .then(function (engines) {
            if (engines === null) return;
            if (engines.length === 0) { emReset(selEngine, 'Engine (optional)'); selEngine.disabled = false; return; }
            emPopulate(selEngine, engines, 'Engine');
            if (vehicle.engine) selEngine.value = vehicle.engine;
            updateSaveBtn();
          })
          .catch(function () { updateSaveBtn(); });
      }
    }
    updateSaveBtn();

    // Bumped on every cascade change so slower, superseded API responses are ignored.
    var seqNow = 0;

    // cascade: year change
    selYear.addEventListener('change', function () {
      if (rulesApi()) {
        var prevMake = selMake.value;
        emPopulate(selMake, rulesApi().getMakes(selYear.value), 'Make *');
        if (prevMake) selMake.value = prevMake;
      }
      if (selMake.value) { selMake.dispatchEvent(new Event('change')); return; }
      ++seqNow;
      emReset(selModel, 'Model *');
      emReset(selTrim,  'Trim');
      emReset(selEngine, 'Engine');
      updateSaveBtn();
    });
    // cascade: make change
    selMake.addEventListener('change', function () {
      var seq = ++seqNow;
      emLoading(selModel);
      emReset(selTrim,   'Trim');
      emReset(selEngine, 'Engine');
      updateSaveBtn();
      emFetchModels(selMake.value, selYear.value)
        .then(function (models) { if (seq !== seqNow) return; emPopulate(selModel, models, 'Model *'); updateSaveBtn(); })
        .catch(function () { if (seq === seqNow) emReset(selModel, 'Error – retry'); });
    });
    // cascade: model change
    selModel.addEventListener('change', function () {
      var seq = ++seqNow;
      emReset(selTrim,   'Trim');
      emReset(selEngine, 'Engine');
      updateSaveBtn();
      var yr = selYear.value, mk = selMake.value, md = selModel.value;
      if (!yr || !mk || !md) return;
      emLoading(selEngine);
      emFetchEnginesForModel(yr, mk, md)
        .then(function (engines) {
          if (seq !== seqNow) return;
          if (!engines || engines.length === 0) { emReset(selEngine, 'Engine (optional)'); selEngine.disabled = false; return; }
          emPopulate(selEngine, engines, 'Engine');
        })
        .catch(function () { if (seq === seqNow) { emReset(selEngine, 'Engine (optional)'); selEngine.disabled = false; } });
    });
    // cascade: trim change
    selTrim.addEventListener('change', function () {
      var seq = ++seqNow;
      emReset(selEngine, 'Engine');
      var yr = selYear.value, mk = selMake.value, tr = selTrim.value;
      if (!yr || !mk || !tr) return;
      emLoading(selEngine);
      emFetchEngines(yr, mk, tr)
        .then(function (engines) {
          if (seq !== seqNow) return;
          if (!engines || engines.length === 0) { emReset(selEngine, 'Engine (optional)'); selEngine.disabled = false; return; }
          emPopulate(selEngine, engines, 'Engine');
        })
        .catch(function () { if (seq === seqNow) { emReset(selEngine, 'Engine (optional)'); selEngine.disabled = false; } });
    });
    selModel.addEventListener('change', updateSaveBtn);

    // tab switching
    overlay.querySelectorAll('.vs-edit-modal__tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        overlay.querySelectorAll('.vs-edit-modal__tab').forEach(function (t) { t.classList.remove('vs-edit-modal__tab--active'); });
        tab.classList.add('vs-edit-modal__tab--active');
        var panel = tab.dataset.panel;
        overlay.querySelector('#vs-edit-panel-mm').style.display = panel === 'mm' ? '' : 'none';
        overlay.querySelector('#vs-edit-panel-vin').style.display = panel === 'vin' ? '' : 'none';
      });
    });

    // VIN decode
    overlay.querySelector('.vs-edit-modal__vin-btn').addEventListener('click', function () {
      var vin = overlay.querySelector('#vs-edit-vin').value.trim().toUpperCase();
      var resultEl = overlay.querySelector('.vs-edit-modal__vin-result');
      if (vin.length !== 17) { resultEl.textContent = 'VIN must be 17 characters.'; return; }
      resultEl.textContent = 'Decoding…';
      fetch(NHTSA_BASE + '/DecodeVinValues/' + encodeURIComponent(vin) + '?format=json')
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var res = (j.Results || [])[0] || {};
          if (!res.Make || !res.Model) { resultEl.textContent = 'Could not decode VIN.'; return; }
          var decoded = { year: res.ModelYear, make: res.Make, model: res.Model };
          var allowedMake = rulesApi() ? rulesApi().matchVehicle(decoded) : res.Make;
          if (!allowedMake) {
            resultEl.textContent = 'Sorry, ' + [res.ModelYear, res.Make, res.Model].join(' ') + ' is not supported yet.';
            return;
          }
          res.Make = allowedMake;
          resultEl.textContent = res.ModelYear + ' ' + res.Make + ' ' + res.Model + (res.Trim ? ' (' + res.Trim + ')' : '');
          // switch to MM tab and fill
          overlay.querySelector('[data-panel="mm"]').click();
          emPopulate(selYear, years, 'Year *'); if (res.ModelYear) selYear.value = res.ModelYear;
          emPopulate(selMake, makes, 'Make *'); if (res.Make) selMake.value = res.Make;
          selMake.dispatchEvent(new Event('change'));
          // Wait for the model list from the make change to arrive (up to ~15s).
          var tries = 0;
          var waitModels = setInterval(function () {
            var loading = selModel.disabled && selModel.options[0] && /Loading/.test(selModel.options[0].text);
            if (loading && ++tries < 60) return;
            clearInterval(waitModels);
            if (res.Model) {
              var match = [].filter.call(selModel.options, function (o) {
                return o.value && o.value.toLowerCase() === String(res.Model).toLowerCase();
              })[0];
              if (match) selModel.value = match.value;
            }
            selModel.dispatchEvent(new Event('change'));
          }, 250);
        })
        .catch(function () { resultEl.textContent = 'Decode failed. Check your connection.'; });
    });

    // save
    btnSave.addEventListener('click', function () {
      var v = {
        year: selYear.value, make: selMake.value, model: selModel.value,
        trim: selTrim.value || '', engine: selEngine.value || ''
      };
      if (!v.year || !v.make || !v.model) return;
      var saved = getSaved();
      var label = [v.year, v.make, v.model, v.trim, v.engine].filter(Boolean).join(' ');
      // replace old entry for the same year/make/model (or old vehicle)
      saved = saved.filter(function (s) {
        return !(s.year === vehicle.year && s.make === vehicle.make && s.model === vehicle.model);
      });
      saved = saved.filter(function (s) {
        return !([s.year, s.make, s.model, s.trim, s.engine].filter(Boolean).join(' ') === label);
      });
      saved.unshift(v);
      if (saved.length > 5) saved = saved.slice(0, 5);
      setSaved(saved);
      setActive(v);
      closeEditModal();
      var handle = window.vsCollectionHandle || 'all';
      var url = '/collections/' + handle + '/' + vehicleTagSlugs(v).join('+');
      rememberRequestedFilter(url);
      window.location.href = url;
    });

    function closeEditModal() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onEsc);
    }
    function onEsc(e) { if (e.key === 'Escape') closeEditModal(); }
    overlay.querySelector('.vs-edit-modal__close').addEventListener('click', closeEditModal);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeEditModal(); });
    document.addEventListener('keydown', onEsc);
  }

  function openVehicleModal(activeVehicle) {
    // Delegate to the header modal (vehicle-selector snippet)
    var triggerBtn = document.getElementById('vs-trigger-btn');
    if (triggerBtn) {
      triggerBtn.click();
      return;
    }
    // Fallback: navigate to homepage if header modal not present
    window.location.href = '/';
  }

  function _openVehicleModal_UNUSED(activeVehicle) {
    if (document.getElementById('vs-vehicle-modal')) return;

    var saved  = getSaved();
    var others = saved.filter(function (v) {
      return !(v.year === activeVehicle.year && v.make === activeVehicle.make && v.model === activeVehicle.model);
    });

    var activeTitle = [activeVehicle.year, activeVehicle.make, activeVehicle.model].filter(Boolean).join(' ');
    var activeSub   = [activeVehicle.trim, activeVehicle.engine].filter(Boolean).join(' / ');

    var savedListHTML = '';
    others.forEach(function (v, i) {
      var t = [v.year, v.make, v.model].filter(Boolean).join(' ');
      var s = [v.trim, v.engine].filter(Boolean).join(' / ');
      savedListHTML +=
        '<div class="vs-modal__saved-item" data-idx="' + i + '">' +
          '<div class="vs-modal__saved-car">' + carSVG() + '</div>' +
          '<div class="vs-modal__saved-info">' +
            '<strong>' + t + '</strong>' +
            (s ? '<span>' + s + '</span>' : '') +
          '</div>' +
        '</div>';
    });

    var overlay = document.createElement('div');
    overlay.id = 'vs-vehicle-modal';
    overlay.className = 'vs-modal-overlay';
    overlay.innerHTML =
      '<div class="vs-modal" role="dialog" aria-modal="true">' +
        '<div class="vs-modal__header">' +
          '<h3 class="vs-modal__title">Select your vehicle</h3>' +
          '<button class="vs-modal__close" aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="vs-modal__body">' +
          '<div class="vs-modal__left">' +
            '<p class="vs-modal__section-label">Currently shopping for:</p>' +
            '<div class="vs-modal__current">' +
              '<div class="vs-modal__current-car">' + carSVG() + '</div>' +
              '<div class="vs-modal__current-info">' +
                '<strong>' + activeTitle + '</strong>' +
                (activeSub ? '<span>' + activeSub + '</span>' : '') +
              '</div>' +
              '<div class="vs-modal__current-actions">' +
                '<button class="vs-modal__icon-btn vs-modal__edit-btn" title="Edit vehicle">' +
                  '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>' +
                '</button>' +
                '<button class="vs-modal__icon-btn vs-modal__delete-btn" title="Delete vehicle">' +
                  '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>' +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
          (others.length > 0 ?
            '<div class="vs-modal__divider"></div>' +
            '<div class="vs-modal__right">' +
              '<p class="vs-modal__section-label">Previously saved:</p>' +
              '<div class="vs-modal__saved-list">' + savedListHTML + '</div>' +
            '</div>' : '') +
        '</div>' +
        '<div class="vs-modal__footer">' +
          '<button class="vs-modal__shop-without">Shop without vehicle</button>' +
          '<button class="vs-modal__add-vehicle">Add vehicle</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    function closeModal() {
      var m = document.getElementById('vs-vehicle-modal');
      if (m) m.remove();
    }

    // Close on backdrop click
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });

    overlay.querySelector('.vs-modal__close').addEventListener('click', closeModal);

    // Escape key
    function onKey(e) {
      if (e.key === 'Escape') { closeModal(); document.removeEventListener('keydown', onKey); }
    }
    document.addEventListener('keydown', onKey);

    // Shop without vehicle
    overlay.querySelector('.vs-modal__shop-without').addEventListener('click', function () {
      setActive(null);
      document.dispatchEvent(new CustomEvent('vehicleCleared'));
      closeModal();
      var base = getCollectionBase();
      if (base) window.location.href = base;
    });

    // Add vehicle → scroll to widget
    overlay.querySelector('.vs-modal__add-vehicle').addEventListener('click', function () {
      closeModal();
      var widget = document.getElementById('vehicle-fitment-bar');
      if (widget) {
        widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var btn = document.getElementById('vs-btn-change');
        if (btn) btn.click();
      } else {
        window.location.href = '/';
      }
    });

    // Edit active vehicle
    var editBtn = overlay.querySelector('.vs-modal__edit-btn');
    if (editBtn) {
      editBtn.addEventListener('click', function () {
        closeModal();
        openEditVehicleModal(activeVehicle);
      });
    }

    // Delete active vehicle
    var delBtn = overlay.querySelector('.vs-modal__delete-btn');
    if (delBtn) {
      delBtn.addEventListener('click', function () {
        var list = getSaved();
        var newList = list.filter(function (v) {
          return !(v.year === activeVehicle.year && v.make === activeVehicle.make && v.model === activeVehicle.model);
        });
        setSaved(newList);
        if (newList.length > 0) {
          setActive(newList[0]);
          closeModal();
          var nv = newList[0];
          window.location.href = '/collections/' +
            (window.location.pathname.match(/^\/collections\/([^/]+)/) || ['', 'all'])[1] +
            '/' + vehicleTagSlugs(nv).join('+');
        } else {
          setActive(null);
          document.dispatchEvent(new CustomEvent('vehicleCleared'));
          closeModal();
          var base = getCollectionBase();
          if (base) window.location.href = base;
        }
      });
    }

    // Switch to a previously saved vehicle
    var savedItems = overlay.querySelectorAll('.vs-modal__saved-item');
    savedItems.forEach(function (item) {
      item.addEventListener('click', function () {
        var sv = others[parseInt(item.getAttribute('data-idx'), 10)];
        setActive(sv);
        closeModal();
        window.location.href = '/collections/' +
          (window.location.pathname.match(/^\/collections\/([^/]+)/) || ['', 'all'])[1] +
          '/' + vehicleTagSlugs(sv).join('+');
      });
    });
  } // end _openVehicleModal_UNUSED

  // ── render bar ─────────────────────────────────────────────────────────────

  function renderBar(vehicle) {
    var existing = document.getElementById('vs-fitment-notice');
    if (existing) return;

    var collectionBase = getCollectionBase();
    if (!collectionBase) return;

    var filtered    = isAlreadyFiltered(vehicle);
    var filterUrl   = buildTagUrl(collectionBase, vehicle);
    var clearUrl    = collectionBase + window.location.search;

    var vehicleTitle    = escHtml([vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '));
    var vehicleSubtitle = escHtml([vehicle.trim, vehicle.engine].filter(Boolean).join(' / '));

    var bar = document.createElement('div');
    bar.id = 'vs-fitment-notice';
    bar.className = 'vs-fitment-notice' + (filtered ? ' vs-fitment-notice--active' : '');

    if (filtered) {
      // ── Results header (Shopping for your vehicle style) ──────────────────
      bar.innerHTML =
        '<div class="vs-fitment-notice__results">' +
          '<div class="vs-fitment-notice__results-left">' +
            '<div class="vs-fitment-notice__car-wrap">' +
              carSVG() +
            '</div>' +
            '<div class="vs-fitment-notice__results-text">' +
              '<strong class="vs-fitment-notice__results-vehicle">Shopping for your ' + vehicleTitle + '</strong>' +
              (vehicleSubtitle ? '<span class="vs-fitment-notice__results-sub">' + vehicleSubtitle + '</span>' : '') +
            '</div>' +
          '</div>' +
          '<div class="vs-fitment-notice__results-right">' +
            '<button class="vs-fitment-notice__btn vs-fitment-notice__btn--change" id="vs-notice-change">CHANGE VEHICLE</button>' +
            '<a href="' + clearUrl + '" class="vs-fitment-notice__btn vs-fitment-notice__btn--clear">SHOP WITHOUT VEHICLE</a>' +
          '</div>' +
        '</div>';
    } else {
      // ── Unfiltered prompt ─────────────────────────────────────────────────
      bar.innerHTML =
        '<span class="vs-fitment-notice__icon">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
        '</span>' +
        '<span class="vs-fitment-notice__text">' +
          'Your saved vehicle: <strong>' + vehicleTitle + '</strong> &mdash; see compatible parts only?' +
        '</span>' +
        '<span class="vs-fitment-notice__actions">' +
          '<a href="' + filterUrl + '" class="vs-fitment-notice__btn vs-fitment-notice__btn--apply">Filter by my vehicle</a>' +
          '<button class="vs-fitment-notice__dismiss" aria-label="Dismiss">\u00d7</button>' +
        '</span>';
    }

    var applyLink = bar.querySelector('.vs-fitment-notice__btn--apply');
    if (applyLink) {
      applyLink.addEventListener('click', function () { rememberRequestedFilter(filterUrl); });
    }

    var dismissBtn = bar.querySelector('.vs-fitment-notice__dismiss');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        bar.remove();
        sessionStorage.setItem(DISMISS_KEY, '1');
      });
    }

    var changeBtn = bar.querySelector('#vs-notice-change');
    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        openVehicleModal(vehicle);
      });
    }

    // Insert before the product grid
    var grid = document.getElementById('main-collection-product-grid');
    var parent = document.querySelector('.vs-tag-empty') ||
      (grid ? grid.closest('.collection') || grid.parentNode : null);
    if (parent) {
      parent.parentNode.insertBefore(bar, parent);
    } else {
      var main = document.querySelector('main') || document.body;
      main.insertBefore(bar, main.firstChild);
    }
  }

  // ── init ───────────────────────────────────────────────────────────────────

  // ── Product page fitment banner ────────────────────────────────────────────

  function checkProductFit(vehicle, tags) {
    if (!tags || tags.length === 0) return null; // unknown
    var slugs    = tags.map(toSlug);
    var hasMake  = slugs.indexOf(toSlug(vehicle.make))  !== -1;
    var hasModel = slugs.indexOf(toSlug(vehicle.model)) !== -1;
    if (!hasMake && !hasModel) return null; // product has no fitment tags for this vehicle
    // Year only constrains products that carry year tags at all.
    var years   = slugs.filter(function (s) { return /^(19|20)\d{2}$/.test(s); });
    var yearOk  = !vehicle.year || years.length === 0 || years.indexOf(String(vehicle.year)) !== -1;
    // Same rule as the collection filter: a selected engine must be tagged on the product.
    var engineOk = !vehicle.engine || slugs.indexOf(toSlug(vehicle.engine)) !== -1;
    return hasMake && hasModel && yearOk && engineOk; // true = fits, false = doesn't fit
  }

  function renderProductBanner(vehicle) {
    var existing = document.getElementById('vs-product-fitment');
    if (existing) existing.remove();

    var tags = window.VehProductTags || [];
    var fits = checkProductFit(vehicle, tags);
    var vehicleTitle = escHtml([vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '));

    var banner = document.createElement('div');
    banner.id = 'vs-product-fitment';

    if (fits === null) {
      // No fitment tags on product — show neutral "Check" banner
      banner.className = 'vs-product-fitment vs-product-fitment--check';
      banner.innerHTML =
        '<span class="vs-product-fitment__icon">' +
          '<svg width="20" height="20" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M32.637 12.307h4.03v2.538h-2.84l1.224 1.347A5.292 5.292 0 0 1 36.667 20v11.07a2.265 2.265 0 0 1-2.263 2.263h-7.31a2.265 2.265 0 0 1-2.262-2.262v-1.726H14.814v1.726c0 1.247-.661 2.262-1.909 2.262h-7.31a2.265 2.265 0 0 1-2.262-2.262V20c0-1.484.615-2.84 1.615-3.808l1.49-1.347H3.333v-2.538h4.12l.807-1.69a5.3 5.3 0 0 1 5.123-3.95h13.39a5.32 5.32 0 0 1 3.28 1.137 5.32 5.32 0 0 1 1.871 2.922l.713 1.58Zm-.898 4.92-.852-.111-21.75.006-.845.102A2.795 2.795 0 0 0 5.834 20v1.082h6.494v2.502H5.834v7.248h6.832v-3.989h14.667v3.989h6.832v-7.248h-6.493v-2.502h6.493V20a2.802 2.802 0 0 0-2.426-2.772ZM9 14.66h22.082l-1.589-3.35a2.788 2.788 0 0 0-2.719-2.142h-13.39c-1.27 0-2.381.858-2.704 2.086l-1.68 3.406Z" fill="currentColor"/></svg>' +
        '</span>' +
        '<span class="vs-product-fitment__text">Checking fitment for <strong>' + vehicleTitle + '</strong></span>' +
        '<button class="vs-product-fitment__action" id="vs-product-fitment-change">Change vehicle</button>';
    } else if (fits) {
      banner.className = 'vs-product-fitment vs-product-fitment--fits';
      banner.innerHTML =
        '<span class="vs-product-fitment__icon vs-product-fitment__icon--check">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>' +
        '</span>' +
        '<span class="vs-product-fitment__text">Fits your <strong>' + vehicleTitle + '</strong></span>' +
        '<button class="vs-product-fitment__action" id="vs-product-fitment-change">Change vehicle</button>';
    } else {
      banner.className = 'vs-product-fitment vs-product-fitment--no-fit';
      banner.innerHTML =
        '<span class="vs-product-fitment__icon vs-product-fitment__icon--x">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>' +
        '</span>' +
        '<span class="vs-product-fitment__text">May not fit your <strong>' + vehicleTitle + '</strong></span>' +
        '<button class="vs-product-fitment__action" id="vs-product-fitment-change">Change vehicle</button>';
    }

    // "Change vehicle" opens header modal
    banner.querySelector('#vs-product-fitment-change').addEventListener('click', function () {
      var triggerBtn = document.getElementById('vs-trigger-btn');
      if (triggerBtn) triggerBtn.click();
    });

    // Insert before the product form / price area
    var insertBefore =
      document.querySelector('.productView-price') ||
      document.querySelector('[id^="product-price-"]') ||
      document.querySelector('.product-form') ||
      document.querySelector('.productView-info') ||
      document.querySelector('.product__info-container');

    if (insertBefore) {
      insertBefore.parentNode.insertBefore(banner, insertBefore);
    }
  }

  function renderProductBannerNoVehicle() {
    var existing = document.getElementById('vs-product-fitment');
    if (existing) existing.remove();

    var banner = document.createElement('div');
    banner.id = 'vs-product-fitment';
    banner.className = 'vs-product-fitment vs-product-fitment--empty';
    banner.innerHTML =
      '<span class="vs-product-fitment__icon">' +
        '<svg width="20" height="20" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M32.637 12.307h4.03v2.538h-2.84l1.224 1.347A5.292 5.292 0 0 1 36.667 20v11.07a2.265 2.265 0 0 1-2.263 2.263h-7.31a2.265 2.265 0 0 1-2.262-2.262v-1.726H14.814v1.726c0 1.247-.661 2.262-1.909 2.262h-7.31a2.265 2.265 0 0 1-2.262-2.262V20c0-1.484.615-2.84 1.615-3.808l1.49-1.347H3.333v-2.538h4.12l.807-1.69a5.3 5.3 0 0 1 5.123-3.95h13.39a5.32 5.32 0 0 1 3.28 1.137 5.32 5.32 0 0 1 1.871 2.922l.713 1.58Zm-.898 4.92-.852-.111-21.75.006-.845.102A2.795 2.795 0 0 0 5.834 20v1.082h6.494v2.502H5.834v7.248h6.832v-3.989h14.667v3.989h6.832v-7.248h-6.493v-2.502h6.493V20a2.802 2.802 0 0 0-2.426-2.772ZM9 14.66h22.082l-1.589-3.35a2.788 2.788 0 0 0-2.719-2.142h-13.39c-1.27 0-2.381.858-2.704 2.086l-1.68 3.406Z" fill="currentColor"/></svg>' +
      '</span>' +
      '<span class="vs-product-fitment__text">Check if this fits your vehicle</span>' +
      '<button class="vs-product-fitment__action vs-product-fitment__action--add" id="vs-product-fitment-add">Add vehicle</button>';

    banner.querySelector('#vs-product-fitment-add').addEventListener('click', function () {
      var triggerBtn = document.getElementById('vs-trigger-btn');
      if (triggerBtn) { triggerBtn.click(); return; }
      window.location.href = '/';
    });

    var insertBefore =
      document.querySelector('.productView-price') ||
      document.querySelector('[id^="product-price-"]') ||
      document.querySelector('.product-form') ||
      document.querySelector('.productView-info') ||
      document.querySelector('.product__info-container');

    if (insertBefore) {
      insertBefore.parentNode.insertBefore(banner, insertBefore);
    }
  }

  function initProductFitment() {
    if (!/^\/products\//.test(window.location.pathname)) return;

    var vehicle = getActive();
    if (vehicle && vehicle.make && vehicle.model) {
      renderProductBanner(vehicle);
    } else {
      renderProductBannerNoVehicle();
    }

    document.addEventListener('vehicleSelected', function (e) {
      renderProductBanner(e.detail);
    });
    document.addEventListener('vehicleCleared', function () {
      renderProductBannerNoVehicle();
    });
  }

  // ── Collection list page banner ─────────────────────────────────────────────

  function initCollectionListBanner() {
    if (window.location.pathname !== '/collections') return;

    var vehicle = getActive();
    if (!vehicle || !vehicle.make || !vehicle.model) return;

    var existing = document.getElementById('vs-fitment-notice');
    if (existing) return;

    var vehicleTitle = escHtml([vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '));

    var bar = document.createElement('div');
    bar.id = 'vs-fitment-notice';
    bar.className = 'vs-fitment-notice';
    bar.innerHTML =
      '<span class="vs-fitment-notice__icon">' +
        '<svg width="16" height="16" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M32.637 12.307h4.03v2.538h-2.84l1.224 1.347A5.292 5.292 0 0 1 36.667 20v11.07a2.265 2.265 0 0 1-2.263 2.263h-7.31a2.265 2.265 0 0 1-2.262-2.262v-1.726H14.814v1.726c0 1.247-.661 2.262-1.909 2.262h-7.31a2.265 2.265 0 0 1-2.262-2.262V20c0-1.484.615-2.84 1.615-3.808l1.49-1.347H3.333v-2.538h4.12l.807-1.69a5.3 5.3 0 0 1 5.123-3.95h13.39a5.32 5.32 0 0 1 3.28 1.137 5.32 5.32 0 0 1 1.871 2.922l.713 1.58Zm-.898 4.92-.852-.111-21.75.006-.845.102A2.795 2.795 0 0 0 5.834 20v1.082h6.494v2.502H5.834v7.248h6.832v-3.989h14.667v3.989h6.832v-7.248h-6.493v-2.502h6.493V20a2.802 2.802 0 0 0-2.426-2.772ZM9 14.66h22.082l-1.589-3.35a2.788 2.788 0 0 0-2.719-2.142h-13.39c-1.27 0-2.381.858-2.704 2.086l-1.68 3.406Z" fill="currentColor"/></svg>' +
      '</span>' +
      '<span class="vs-fitment-notice__text">Shopping for: <strong>' + vehicleTitle + '</strong></span>' +
      '<span class="vs-fitment-notice__actions">' +
        '<button class="vs-fitment-notice__btn vs-fitment-notice__btn--change" id="vs-list-change">Change vehicle</button>' +
        '<button class="vs-fitment-notice__dismiss" aria-label="Dismiss">\u00d7</button>' +
      '</span>';

    bar.querySelector('.vs-fitment-notice__dismiss').addEventListener('click', function () {
      bar.remove();
    });
    bar.querySelector('#vs-list-change').addEventListener('click', function () {
      var triggerBtn = document.getElementById('vs-trigger-btn');
      if (triggerBtn) triggerBtn.click();
    });

    var main = document.querySelector('main') || document.body;
    main.insertBefore(bar, main.firstChild);
  }

  function init() {
    // Collection list page
    initCollectionListBanner();

    // Only run on collection pages
    if (!/^\/collections\//.test(window.location.pathname)) {
      // Product page
      initProductFitment();
      return;
    }

    // Respect session dismiss
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    var vehicle = getActive();
    if (!vehicle || !vehicle.make || !vehicle.model) return;

    renderBar(vehicle);

    // Re-render if vehicle changes (e.g. user edits from fitment bar)
    document.addEventListener('vehicleSelected', function (e) {
      var existing = document.getElementById('vs-fitment-notice');
      if (existing) existing.remove();
      sessionStorage.removeItem(DISMISS_KEY);
      renderBar(e.detail);
    });
    document.addEventListener('vehicleCleared', function () {
      var existing = document.getElementById('vs-fitment-notice');
      if (existing) existing.remove();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
